import { useEffect, useRef, useState } from "react";
import { Loader2, Save, RotateCcw, Mail, MessageCircle, Braces } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";

const FIELDS = [
  { k: "email_subject", label: "Subjek email", rows: 1 },
  { k: "email_body", label: "Isi email", rows: 8 },
  { k: "wa", label: "Pesan WhatsApp", rows: 7 },
];

const fill = (tpl, vars) => (tpl || "").replace(/\{(\w+)\}/g, (m, k) => vars[k] ?? m);

function Rich({ text }) {
  return <>{text.split("\n").map((line, i) => (
    <span key={i}>{line.split(/(\*[^*\n]+\*)/g).map((p, j) => (p.startsWith("*") && p.endsWith("*") && p.length > 2 ? <b key={j}>{p.slice(1, -1)}</b> : p))}<br /></span>
  ))}</>;
}

function Preview({ draft, vars }) {
  const [tab, setTab] = useState("email");
  return (
    <div className="tpl-preview" data-testid="tpl-preview">
      <div className="tpl-preview-tabs">
        <button className={tab === "email" ? "active" : ""} onClick={() => setTab("email")} data-testid="tpl-preview-email"><Mail size={13} /> Email</button>
        <button className={tab === "wa" ? "active" : ""} onClick={() => setTab("wa")} data-testid="tpl-preview-wa"><MessageCircle size={13} /> WhatsApp</button>
      </div>
      {tab === "email" ? (
        <div className="tpl-mail"><small>Subjek</small><strong data-testid="tpl-preview-subject">{fill(draft.email_subject, vars)}</strong>
          <div className="tpl-mail-body"><Rich text={fill(draft.email_body, vars)} /></div></div>
      ) : (
        <div className="tpl-wa"><div className="tpl-wa-bubble" data-testid="tpl-preview-wa-text"><Rich text={fill(draft.wa, vars)} /></div></div>
      )}
      <small className="fin-muted">Pratinjau memakai data contoh. Teks di antara *bintang* ditebalkan.</small>
    </div>
  );
}

export function ReminderTemplates() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("invoices:create");
  const [s, setS] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const focus = useRef({ k: "wa", el: null });

  const apply = (d) => { setS(d); setDraft({ ...d.templates }); };
  useEffect(() => { api.get("/finance/settings").then((r) => apply(r.data.data)).catch((e) => toast.error(apiError(e))); }, []);

  const insert = (key) => {
    const { k, el } = focus.current;
    const tok = `{${key}}`;
    const cur = draft[k] || "";
    const at = el && el.selectionStart != null ? el.selectionStart : cur.length;
    setDraft({ ...draft, [k]: cur.slice(0, at) + tok + cur.slice(el?.selectionEnd ?? at) });
    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(at + tok.length, at + tok.length); } }, 0);
  };
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/finance/settings/templates", draft); apply(r.data.data); toast.success("Template pesan pengingat disimpan"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!s) return <div className="panel"><div className="rj-loading"><Loader2 className="spin" /> Memuat…</div></div>;
  const vars = Object.fromEntries(s.template_vars.map((v) => [v.key, v.sample]));
  const dirty = JSON.stringify(draft) !== JSON.stringify(s.templates);

  return (
    <div className="panel reminder-panel tpl-panel" data-testid="reminder-templates">
      <div className="panel-head"><div><h3><Braces size={16} style={{ verticalAlign: -3 }} /> Template Pesan Pengingat</h3>
        <p>Satu template dipakai untuk semua tahap pengingat (H-3 s/d hari jatuh tempo). Klik variabel untuk menyisipkannya di posisi kursor.</p></div></div>
      <div className="tpl-vars" data-testid="tpl-vars">
        {s.template_vars.map((v) => (
          <button key={v.key} type="button" className="tpl-var" title={`${v.label} — contoh: ${v.sample}`} onClick={() => insert(v.key)} disabled={!canEdit} data-testid={`tpl-var-${v.key}`}>{`{${v.key}}`}</button>
        ))}
      </div>
      <div className="tpl-grid">
        <div className="tpl-fields">
          {FIELDS.map((f) => {
            const Tag = f.rows > 1 ? "textarea" : "input";
            return (
              <label key={f.k} className="rj-field">{f.label}{s.templates_custom?.[f.k] && <span className="tpl-custom">diubah</span>}
                <Tag className="rj-input" rows={f.rows} value={draft[f.k]} disabled={!canEdit}
                  onFocus={(e) => { focus.current = { k: f.k, el: e.target }; }} onSelect={(e) => { focus.current = { k: f.k, el: e.target }; }}
                  onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })} data-testid={`tpl-${f.k}`} />
              </label>
            );
          })}
        </div>
        <Preview draft={draft} vars={vars} />
      </div>
      {canEdit && (
        <div className="reminder-actions">
          <button className="rj-btn rj-btn-green" onClick={save} disabled={saving || !dirty} data-testid="tpl-save-btn">
            {saving ? <Loader2 size={16} className="spin" /> : <><Save size={16} /> Simpan Template</>}
          </button>
          <button className="rj-btn rj-btn-ghost" onClick={() => setDraft({ ...s.template_defaults })} data-testid="tpl-reset-btn"><RotateCcw size={16} /> Pakai template bawaan</button>
        </div>
      )}
      {s.templates_updated_at && <p className="fin-muted" style={{ marginTop: 10 }}>Terakhir diubah {s.templates_updated_by} · {fmtDateTime(s.templates_updated_at)}</p>}
    </div>
  );
}
